import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Key, Copy, Trash2, Plus, Eye, EyeOff, User, GitBranch, Terminal } from 'lucide-react';
import { listApiTokens, createApiToken, revokeApiToken, listRepositories, deleteRepository } from '../../services/registry';
import API from '../../services/api.ts';
import Navbar from '../navbar/Navbar';
import './Settings.css';
import '../shared/starfield.css';

const Settings = ({ mode = 'all' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(mode === 'profile' ? 'profile' : 'cli');
  const showDevTools = mode === 'all' || mode === 'devtools';
  const showProfile  = mode === 'all' || mode === 'profile';
  const sidebarTitle = mode === 'profile' ? 'Profile' : mode === 'devtools' ? 'Dev Tools' : 'Tools';
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  // Create token modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [creatingToken, setCreatingToken] = useState(false);

  // Newly created token (shown only once)
  const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
  const [showToken, setShowToken] = useState(true);

  // Registry state
  const [repositories, setRepositories] = useState([]);
  const [repoLoading, setRepoLoading] = useState(false);
  const [repoFilter, setRepoFilter] = useState('all'); // 'all', 'mine', 'public'
  const [repoType, setRepoType] = useState('models'); // 'models', 'datasets', 'studies', 'tools'
  const [importing, setImporting] = useState(null);

  useEffect(() => {
    fetchUserInfo();
    fetchTokens();
  }, []);

  // Fetch repositories when registry tab is active
  useEffect(() => {
    if (activeTab === 'registry') {
      fetchRepositories();
    }
  }, [activeTab, repoFilter, repoType]);

  const fetchUserInfo = async () => {
    try {
      const response = await API.get('/accounts/users/me/');
      setUserInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  };

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const response = await listApiTokens();
      setTokens(response.data);
    } catch (err) {
      // Don't show error if 404 (no tokens yet)
      if (err.response?.status !== 404) {
        toast.error('Failed to load API tokens');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRepositories = useCallback(async () => {
    try {
      setRepoLoading(true);
      const options = {};
      if (repoFilter === 'mine') options.mine = true;
      if (repoFilter === 'public') options.public = true;
      // Filter by repository type (model, dataset, study)
      // Convert plural form to singular for API (models -> model, datasets -> dataset, studies -> study)
      if (repoType) {
        const singularType = repoType === 'studies' ? 'study' : repoType.slice(0, -1);
        options.repo_type = singularType;
      }
      const response = await listRepositories(options);
      setRepositories(response.data || response);
    } catch (err) {
      console.error("Error fetching repositories:", err);
      toast.error("Failed to fetch repositories.");
    } finally {
      setRepoLoading(false);
    }
  }, [repoFilter, repoType]);

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error('Please enter a token name');
      return;
    }

    setCreatingToken(true);
    try {
      const response = await createApiToken(newTokenName.trim());
      setNewlyCreatedToken(response.data.token);
      setShowCreateModal(false);
      setNewTokenName('');
      fetchTokens();
      toast.success('API token created successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create token');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (tokenId, tokenName) => {
    if (!window.confirm(`Are you sure you want to revoke the token "${tokenName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await revokeApiToken(tokenId);
      setTokens(tokens.filter(t => t.id !== tokenId));
      toast.success('Token revoked successfully');
    } catch (err) {
      toast.error('Failed to revoke token');
    }
  };

  const handleDeleteRepo = async (repo) => {
    if (!window.confirm(`Are you sure you want to delete "${repo.full_name}"? This cannot be undone.`)) return;

    try {
      const [owner, name] = repo.full_name.split('/');
      await deleteRepository(owner, name);
      toast.success(`Repository "${repo.full_name}" deleted`);
      fetchRepositories();
    } catch (err) {
      console.error("Error deleting repository:", err);
      toast.error(err.response?.data?.error || "Failed to delete repository");
    }
  };

  const handleImportModel = async (repo) => {
    try {
      setImporting(repo.id);
      const response = await API.post('/ai-models/models/import-from-registry/', {
        full_name: repo.full_name
      });

      if (response.data.already_exists) {
        toast.info("Model already imported from this repository");
        navigate('/Models');
      } else {
        toast.success(`Model "${response.data.model.name}" imported and build started!`);
        navigate('/Models');
      }
    } catch (err) {
      console.error("Error importing model:", err);
      toast.error(err.response?.data?.error || "Failed to import model");
    } finally {
      setImporting(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModelTypeIcon = (type) => {
    const icons = {
      segmentation: 'fa-object-group',
      classification: 'fa-tags',
      detection: 'fa-crosshairs',
      generation: 'fa-magic',
      other: 'fa-cube'
    };
    return icons[type] || icons.other;
  };

  const getRepoTypeLabel = () => {
    switch (repoType) {
      case 'models': return 'Model';
      case 'datasets': return 'Dataset';
      case 'studies': return 'Study';
      case 'tools': return 'Tool';
      default: return 'Model';
    }
  };

  return (
    <div className="settings-page">
      {/* Starfield Background */}
      <div className="starfield-background"></div>

      <Navbar />

      <div className="container-fluid py-4">
        <div className="row">
          {/* Sidebar */}
          <div className="col-md-3 col-lg-2">
            <div className="settings-sidebar">
              <h5 className="sidebar-title">{sidebarTitle}</h5>
              <nav className="settings-nav">
                {showDevTools && (
                  <>
                    <button
                      className={`settings-nav-item ${activeTab === 'cli' ? 'active' : ''}`}
                      onClick={() => setActiveTab('cli')}
                    >
                      <Terminal size={18} />
                      <span>Discovery CLI</span>
                    </button>
                    <button
                      className={`settings-nav-item ${activeTab === 'tokens' ? 'active' : ''}`}
                      onClick={() => setActiveTab('tokens')}
                    >
                      <Key size={18} />
                      <span>API Tokens</span>
                    </button>
                    <button
                      className={`settings-nav-item ${activeTab === 'registry' ? 'active' : ''}`}
                      onClick={() => setActiveTab('registry')}
                    >
                      <GitBranch size={18} />
                      <span>Registry</span>
                    </button>
                  </>
                )}
                {showProfile && (
                  <button
                    className={`settings-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                  >
                    <User size={18} />
                    <span>Profile</span>
                  </button>
                )}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-9 col-lg-10">
            {activeTab === 'cli' && (
              <div className="settings-section">
                <div className="section-header">
                  <div>
                    <h4>
                      <Terminal size={24} className="me-2" style={{ display: 'inline' }} />
                      Discovery CLI
                    </h4>
                    <p className="text-muted">
                      Command-line tools for pushing models, datasets, and studies.
                    </p>
                  </div>
                  <a
                    href="/api/registry/cli/download/"
                    className="btn btn-success"
                    download
                  >
                    <i className="fa fa-download me-2"></i>
                    Download CLI
                  </a>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6 mb-3">
                    <h6 className="mb-2">
                      <i className="fa fa-download me-2" style={{ color: '#238636' }}></i>
                      Installation
                    </h6>
                    <div className="code-block">
                      <code>
                        # Install the Discovery CLI<br />
                        pip install discovery-cli<br /><br />
                        # Or download and install manually<br />
                        unzip discovery-cli.zip<br />
                        pip install -e ./discovery-cli<br /><br />
                        # Login to your server<br />
                        dv login --server {window.location.origin}<br /><br />
                        # Use --insecure for self-signed certs<br />
                        dv login --server {window.location.origin} --insecure
                      </code>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <h6 className="mb-2">
                      <i className="fa fa-rocket me-2" style={{ color: '#238636' }}></i>
                      Quick Start
                    </h6>
                    <div className="code-block">
                      <code>
                        # Push a model<br />
                        dv models init my-model --type segmentation<br />
                        cd my-model && dv models push --create<br /><br />
                        # Push a dataset<br />
                        dv datasets init my-dataset -m imaging -m annotations<br />
                        cd my-dataset && dv datasets push --create<br /><br />
                        # Push a study<br />
                        dv studies init my-study --dataset my-dataset<br />
                        cd my-study && dv studies push --create<br /><br />
                        # Pull resources from registry<br />
                        dv models pull username/model-name<br />
                        dv datasets pull username/dataset-name<br />
                        dv studies pull username/study-name
                      </code>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <h6 className="mb-2">
                    <i className="fa fa-list-ul me-2" style={{ color: '#238636' }}></i>
                    Available Commands
                  </h6>
                  {[
                    {
                      label: 'Authentication',
                      icon: null,
                      items: [
                        { cmd: 'dv login', desc: 'Authenticate with server' },
                        { cmd: 'dv logout', desc: 'Remove credentials' },
                        { cmd: 'dv whoami', desc: 'Show current user' },
                      ],
                    },
                    {
                      label: 'Models',
                      icon: 'fa-cube',
                      items: [
                        { cmd: 'dv models init', desc: 'Initialize model' },
                        { cmd: 'dv models push', desc: 'Push to registry' },
                        { cmd: 'dv models pull', desc: 'Pull from registry' },
                        { cmd: 'dv models list', desc: 'List models' },
                      ],
                    },
                    {
                      label: 'Datasets',
                      icon: 'fa-database',
                      items: [
                        { cmd: 'dv datasets init', desc: 'Initialize dataset' },
                        { cmd: 'dv datasets push', desc: 'Push to registry' },
                        { cmd: 'dv datasets pull', desc: 'Pull from registry' },
                        { cmd: 'dv datasets list', desc: 'List datasets' },
                      ],
                    },
                    {
                      label: 'Studies',
                      icon: 'fa-book',
                      items: [
                        { cmd: 'dv studies init', desc: 'Initialize study' },
                        { cmd: 'dv studies push', desc: 'Push to registry' },
                        { cmd: 'dv studies pull', desc: 'Pull with bundle' },
                        { cmd: 'dv studies list', desc: 'List studies' },
                      ],
                    },
                  ].map((group) => (
                    <div key={group.label} className="mb-3">
                      <small className="text-muted fw-bold d-block mb-2">
                        {group.icon && <i className={`fa ${group.icon} me-1`}></i>}
                        {group.label}
                      </small>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {group.items.map(({ cmd, desc }) => (
                          <div
                            key={cmd}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: '#f6f8fa',
                              border: '1px solid #d0d7de',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              color: '#24292f',
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                                color: '#0969da',
                                fontWeight: 500,
                              }}
                            >
                              {cmd}
                            </span>
                            <span style={{ color: '#57606a' }}>{desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'tokens' && (
              <div className="settings-section">
                <div className="section-header">
                  <div>
                    <h4>API Tokens</h4>
                    <p className="text-muted">
                      Manage API tokens for CLI and SDK access to the Model Registry.
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus size={18} className="me-1" />
                    New Token
                  </button>
                </div>

                {/* Newly Created Token Alert */}
                {newlyCreatedToken && (
                  <div className="alert alert-warning token-created-alert">
                    <div className="d-flex align-items-start">
                      <Key size={24} className="me-3 mt-1" />
                      <div className="flex-grow-1">
                        <h6 className="alert-heading mb-2">
                          New API Token Created
                        </h6>
                        <p className="mb-2">
                          Copy your token now. You won't be able to see it again!
                        </p>
                        <div className="token-display">
                          <code className="token-value">
                            {showToken ? newlyCreatedToken : '•'.repeat(newlyCreatedToken.length)}
                          </code>
                          <div className="token-actions">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setShowToken(!showToken)}
                              title={showToken ? 'Hide token' : 'Show token'}
                            >
                              {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => copyToClipboard(newlyCreatedToken)}
                              title="Copy to clipboard"
                            >
                              <Copy size={16} />
                            </button>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-link text-muted p-0 mt-2"
                          onClick={() => setNewlyCreatedToken(null)}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tokens List */}
                <div className="tokens-list">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : tokens.length === 0 ? (
                    <div className="empty-state">
                      <Key size={48} className="text-muted mb-3" />
                      <h5>No API tokens yet</h5>
                      <p className="text-muted">
                        Create a token to use the CLI or SDK with the Model Registry.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateModal(true)}
                      >
                        Create your first token
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Token</th>
                            <th>Created</th>
                            <th>Last Used</th>
                            <th>Expires</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {tokens.map(token => (
                            <tr key={token.id}>
                              <td>
                                <strong>{token.name}</strong>
                              </td>
                              <td>
                                <code className="token-prefix">{token.token_prefix}...</code>
                              </td>
                              <td className="text-muted">
                                {formatDate(token.created_at)}
                              </td>
                              <td className="text-muted">
                                {formatDate(token.last_used_at)}
                              </td>
                              <td>
                                {token.expires_at ? (
                                  <span className={new Date(token.expires_at) < new Date() ? 'text-danger' : ''}>
                                    {formatDate(token.expires_at)}
                                  </span>
                                ) : (
                                  <span className="text-muted">Never</span>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRevokeToken(token.id, token.name)}
                                  title="Revoke token"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Usage Instructions */}
                <div className="usage-instructions mt-4">
                  <h6>Using your API token</h6>
                  <div className="code-block">
                    <code>
                      # Install the CLI<br />
                      pip install discovery-cli<br /><br />
                      # Login with your token<br />
                      dv login --server {window.location.origin}
                    </code>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'registry' && (
              <div className="settings-section">
                <div className="section-header">
                  <div>
                    <h4>
                      <GitBranch size={24} className="me-2" style={{ display: 'inline' }} />
                      Registry
                    </h4>
                    <p className="text-muted">
                      Manage your repositories. Push models, datasets, and studies using the CLI.
                    </p>
                  </div>
                  <a
                    href="/api/registry/cli/download/"
                    className="btn btn-success"
                    download
                  >
                    <i className="fa fa-download me-2"></i>
                    Download CLI
                  </a>
                </div>

                {/* Repository Type Tabs */}
                <div className="registry-type-tabs mb-3">
                  <button
                    className={`registry-type-tab ${repoType === 'models' ? 'active' : ''}`}
                    onClick={() => setRepoType('models')}
                  >
                    <i className="fa fa-cube me-2"></i>
                    Models
                  </button>
                  <button
                    className={`registry-type-tab ${repoType === 'datasets' ? 'active' : ''}`}
                    onClick={() => setRepoType('datasets')}
                  >
                    <i className="fa fa-database me-2"></i>
                    Datasets
                  </button>
                  <button
                    className={`registry-type-tab ${repoType === 'studies' ? 'active' : ''}`}
                    onClick={() => setRepoType('studies')}
                  >
                    <i className="fa fa-book me-2"></i>
                    Studies
                  </button>
                  <button
                    className={`registry-type-tab ${repoType === 'tools' ? 'active' : ''}`}
                    onClick={() => setRepoType('tools')}
                  >
                    <i className="fa fa-wrench me-2"></i>
                    Tools
                  </button>
                </div>

                {/* Stats Row */}
                <div className="registry-stats-row mb-3">
                  <div className="registry-stat">
                    <span className="stat-value">{repositories.length}</span>
                    <span className="stat-label">Total</span>
                  </div>
                  <div className="registry-stat">
                    <span className="stat-value">{repositories.filter(r => r.is_owner).length}</span>
                    <span className="stat-label">Mine</span>
                  </div>
                  <div className="registry-stat">
                    <span className="stat-value">{repositories.filter(r => r.is_public).length}</span>
                    <span className="stat-label">Public</span>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="registry-filter-tabs mb-3">
                  <button
                    className={`filter-tab ${repoFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setRepoFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-tab ${repoFilter === 'mine' ? 'active' : ''}`}
                    onClick={() => setRepoFilter('mine')}
                  >
                    My Repositories
                  </button>
                  <button
                    className={`filter-tab ${repoFilter === 'public' ? 'active' : ''}`}
                    onClick={() => setRepoFilter('public')}
                  >
                    Public
                  </button>
                </div>

                {/* Repositories List */}
                <div className="repositories-list">
                  {repoLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : repositories.length === 0 ? (
                    <div className="empty-state">
                      <GitBranch size={48} className="text-muted mb-3" />
                      <h5>No {getRepoTypeLabel().toLowerCase()} repositories yet</h5>
                      <p className="text-muted">
                        Push your first {getRepoTypeLabel().toLowerCase()} using the Discovery CLI.
                      </p>
                      <div className="cli-example-small mt-3">
                        <code>
                          pip install discovery-cli<br />
                          dv login --server {window.location.origin}<br />
                          dv {repoType} init my-{repoType.slice(0, -1)}<br />
                          dv {repoType} push --create
                        </code>
                      </div>
                    </div>
                  ) : (
                    <div className="repo-list">
                      {repositories.map((repo) => (
                        <div key={repo.id} className="repo-list-item">
                          <div className="repo-icon-small">
                            <i className={`fa ${getModelTypeIcon(repo.model_type)}`}></i>
                          </div>
                          <div className="repo-info">
                            <div className="repo-name-row">
                              <span className="repo-name">{repo.full_name}</span>
                              <div className="repo-badges-small">
                                {repo.is_public ? (
                                  <span className="badge badge-public-small">
                                    <i className="fa fa-globe me-1"></i>Public
                                  </span>
                                ) : (
                                  <span className="badge badge-private-small">
                                    <i className="fa fa-lock me-1"></i>Private
                                  </span>
                                )}
                                {repo.ai_model && (
                                  <span className="badge badge-imported-small">
                                    <i className="fa fa-check me-1"></i>Imported
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="repo-description-small">
                              {repo.description || 'No description'}
                            </div>
                            <div className="repo-meta-small">
                              <span><i className="fa fa-user me-1"></i>{repo.owner?.username || 'Unknown'}</span>
                              <span><i className="fa fa-calendar me-1"></i>{new Date(repo.updated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="repo-actions">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => navigate(`/Registry/${repo.full_name}`)}
                              title="View Details"
                            >
                              <i className="fa fa-folder-open"></i>
                            </button>
                            {repoType === 'models' && !repo.ai_model && (
                              <button
                                className="btn btn-sm btn-primary me-2"
                                onClick={() => handleImportModel(repo)}
                                disabled={importing === repo.id}
                                title="Import Model"
                              >
                                {importing === repo.id ? (
                                  <i className="fa fa-spinner fa-spin"></i>
                                ) : (
                                  <i className="fa fa-download"></i>
                                )}
                              </button>
                            )}
                            {repo.is_owner && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteRepo(repo)}
                                title="Delete Repository"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CLI Instructions */}
                <div className="usage-instructions mt-4">
                  <h6>Push a {getRepoTypeLabel()} via CLI</h6>
                  <div className="code-block">
                    <code>
                      # Install the CLI<br />
                      pip install discovery-cli<br /><br />
                      # Login with your token<br />
                      dv login --server {window.location.origin}<br /><br />
                      # Initialize and push<br />
                      dv {repoType} init my-{repoType.slice(0, -1)}<br />
                      dv {repoType} push --create<br /><br />
                      # Pull from registry<br />
                      dv {repoType} pull username/{repoType.slice(0, -1)}-name<br /><br />
                      # List available {repoType}<br />
                      dv {repoType} list --mine
                    </code>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="settings-section">
                <div className="section-header">
                  <div>
                    <h4>Profile</h4>
                    <p className="text-muted">
                      Your account information.
                    </p>
                  </div>
                </div>

                {userInfo && (
                  <div className="profile-info">
                    <div className="profile-field">
                      <label>Username</label>
                      <div className="field-value">{userInfo.username}</div>
                    </div>
                    <div className="profile-field">
                      <label>Email</label>
                      <div className="field-value">{userInfo.email || 'Not set'}</div>
                    </div>
                    <div className="profile-field">
                      <label>Member since</label>
                      <div className="field-value">{formatDate(userInfo.date_joined)}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create API Token</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Token Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., CLI on laptop, CI/CD pipeline"
                    value={newTokenName}
                    onChange={(e) => setNewTokenName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateToken()}
                    autoFocus
                  />
                  <div className="form-text">
                    Give your token a descriptive name so you can identify it later.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateToken}
                  disabled={creatingToken || !newTokenName.trim()}
                >
                  {creatingToken ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creating...
                    </>
                  ) : (
                    'Create Token'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
